/**
 * Who represents this work, as a viewer reads it — OND-73.
 *
 * One read, `fetchWorkRepresentation`, and nothing to do about it: this panel
 * offers no control, because the viewer it exists for may not appoint, end or
 * renew anybody. The owner's cluster, `useWorkAgents.ts`, is where those live.
 * Composed by `useWorkController.ts` beside it; the model hides the panel for
 * a caller who manages the agents, so the two never both draw.
 *
 * A FAILED READ IS AN EMPTY ANSWER, NOT AN ERROR ON THE RECORD. The record's
 * `error` slot is for the acts the reader took; a panel that could not load
 * draws nothing, which is also what a work with no representative draws, and
 * the reader has lost a convenience rather than a fact — the record itself
 * carries nothing that depends on this.
 *
 * THE ANSWER IS KEPT WITH THE WORK IT ANSWERS FOR, and "not loaded yet" is
 * derived from a mismatch rather than stored by resetting state inside the
 * effect — the shape `useMe` uses, and what keeps a stale answer for the
 * previous work from flashing under the next one's heading.
 */

import type { WorkRepresentationResponse } from "@ondo/shared";
import { useEffect, useState } from "react";

import { fetchWorkRepresentation } from "../lib/api.ts";

const EMPTY: WorkRepresentationResponse = { current: [], past: [] };

interface Loaded {
  workId: string;
  answer: WorkRepresentationResponse;
}

export function useWorkRepresentation(workId: string): WorkRepresentationResponse | null {
  const [loaded, setLoaded] = useState<Loaded | null>(null);

  useEffect(() => {
    let active = true;

    fetchWorkRepresentation(workId)
      .then((answer) => {
        if (active) setLoaded({ workId, answer });
      })
      .catch(() => {
        if (active) setLoaded({ workId, answer: EMPTY });
      });

    return () => {
      active = false;
    };
  }, [workId]);

  return loaded?.workId === workId ? loaded.answer : null;
}
